import React, {
  useCallback,
  useContext,
  useState,
  useRef,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Colleague, DealRoomColleagueRole } from 'models/DealRoom';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Loader, Select } from 'ncoded-component-library';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import api from 'api';
import TrashIcon from 'icons/Trash.icon';
import confirm from 'modules/confirm';
import ResolvePopup from 'components/ResolvePopup';
import PencilIcon from 'icons/Pencil.icon';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import {
  checkHasListing,
  checkIsUserSuperAdmin,
  getEditUserRoute,
} from 'utils';
import ThreeDotsDropdown from 'components/ThreeDotsDropdown';
import { DropdownRef } from 'ncoded-component-library/build/components/molecules/Dropdown/Dropdown.component';
import usePastTime from 'hooks/usePastTime';
import ArrowleftIcon from 'icons/Arrowleft.icon';
import TransferArrowsIcon from 'icons/TransferArrows.icon';
import useColleagueRoles from 'hooks/useColleagueRoles';
import { PROFILE_ROUTE } from 'data/global';
import { ALIGNMENT } from 'models/Global';
import DealContext from '../../../../providers/Deal';

import './ColleaguesManageTable.styles.scss';

const COLLEAGUE_PROP_KEYS = [
  'dataRoomAdmin',
  'useMessaging',
  'sendNotifications',
  'showOnListing',
] as const;

const COLLEAGUE_PROPS_COPY = {
  dataRoomAdmin: 'dataRoomAdmin',
  sendNotifications: 'getNotified',
  showOnListing: 'appearOnLisitng',
  useMessaging: 'messageBuyers',
};

const COLLEAGUE_PROPS_TOOLTIP_KEYS = {
  dataRoomAdmin: 'dataRoomAdminTooltipCopy',
  sendNotifications: 'sendNotificationsTooltipCopy',
  showOnListing: 'showOnListingTooltipCopy',
  useMessaging: 'useMessagingTooltipCopy',
};

const MEMBER_COLUMN_STRING = 'member';
const ACTIONS_COLUMN = 'action';

const TABLE_COLUMNS = [
  MEMBER_COLUMN_STRING,
  ...COLLEAGUE_PROP_KEYS,
  ACTIONS_COLUMN,
];

type ColleaguesManageTableProps = {
  className?: string;
  isDisabled: boolean;
  openTransferDrawer?: (colleague: Colleague) => void;
};

export type ColleaguePropertyKeys = typeof COLLEAGUE_PROP_KEYS[number];

const ColleaguesManageTable: React.FC<ColleaguesManageTableProps> = (props) => {
  const { className, isDisabled, openTransferDrawer } = props;

  const [isRequestLoading, setIsRequestLoading] = useState(false);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);
  const {
    isSuperAdmin,
    currentUser: { _id: currentUserId },
  } = useContext(CurrentUserContext);
  const {
    dealRoom: { _id: dealRoomId, name: dealRoomName, listingStage },
    isOwner,
    colleagues,
    setColleagues,
  } = useContext(DealContext);

  const hasListing = useMemo(
    () => checkHasListing(listingStage),
    [listingStage],
  );

  const dropdownRef = useRef<DropdownRef>(null);

  const pastTime = usePastTime();

  const classes = classNames('colleagues-manage-table', className);

  const { t } = useTranslation();

  const colleagueRolesOptions = useColleagueRoles();

  const navigate = useNavigate();

  const changeColleagueValues = useCallback(
    async (colleagueId: string, updatedData: any) => {
      try {
        setIsRequestLoading(true);

        const { data } = await api.dealRoom.updateColleague(
          dealRoomId,
          colleagueId,
          updatedData,
        );

        setColleagues(data);
      } catch (e) {
        popServerError(e);
      } finally {
        setIsRequestLoading(false);
      }
    },
    [dealRoomId, popServerError, setColleagues],
  );

  const onDeleteColleague = useCallback(
    async (colleague: Colleague) => {
      if (
        await confirm(
          {
            title: t('removeColleague'),
            content: t('removeColleagueMessageDR', {
              email: colleague.email,
              dealRoomName: dealRoomName || t('thisDealRoom'),
            }),
            type: 'delete',
            submitBtnText: t('remove'),
            cancelBtnText: t('cancel'),
          },
          true,
        )
      ) {
        try {
          setIsRequestLoading(true);

          const { data } = await api.dealRoom.removeColleague(
            dealRoomId,
            colleague._id,
          );

          setColleagues(data);
        } catch (e) {
          popServerError(e);
        } finally {
          setIsRequestLoading(false);
        }
      }
    },
    [dealRoomId, dealRoomName, popServerError, setColleagues, t],
  );

  const remindColleague = useCallback(
    async (colleagueId: string) => {
      try {
        await api.dealRoom.remindColleague(dealRoomId, colleagueId);

        popSuccess(t('successInvitedColleague'));
      } catch (e) {
        popServerError(e);
      }
    },
    [dealRoomId, popSuccess, popServerError, t],
  );

  const RenderColleagueInfo = useCallback(
    (colleague: Colleague) => {
      const { id, isAccepted, name, email, isOwner, invitedAt } = colleague;

      const invitedLabel = !isAccepted && invitedAt && (
        <span>{pastTime(invitedAt)}</span>
      );

      const RemindButton = !isAccepted && (
        <Button
          variant="link"
          icon={ArrowleftIcon}
          disabled={isDisabled}
          onClick={async (ev) => {
            if (isDisabled) return;
            await remindColleague(id);
          }}
        >
          {t('remind')}
        </Button>
      );

      const nameLabel = (
        <span>
          {isOwner ? (
            <>
              <strong>{isAccepted ? name : t('invited')}</strong> ({t('owner')})
            </>
          ) : isAccepted ? (
            name
          ) : (
            <strong>{t('invited')}</strong>
          )}
        </span>
      );

      return (
        <div className="colleague-content">
          <div className="info">
            <p
              className={classNames(
                'name',
                isAccepted ? 'accepted' : 'invited',
              )}
            >
              {nameLabel}
              {invitedLabel}
              {RemindButton}
            </p>

            {email && <p className="email">{email}</p>}
          </div>
          <Select
            renderAsPortal
            disabled={isDisabled || checkIsUserSuperAdmin(colleague.userRole)}
            value={colleague.role}
            options={colleagueRolesOptions}
            onChange={({ value }: { value: DealRoomColleagueRole }) => {
              changeColleagueValues(colleague._id, { role: value });
            }}
          />
        </div>
      );
    },
    [
      isDisabled,
      colleagueRolesOptions,
      t,
      pastTime,
      remindColleague,
      changeColleagueValues,
    ],
  );

  const RenderColleagueOptions = useCallback(
    (colleague: Colleague) => {
      const isMyProfile = currentUserId === colleague.userId;
      const isColleagueOwner = colleague.isOwner;

      const options = [
        ...(isSuperAdmin
          ? [
              {
                label: t(isMyProfile ? 'editProfile' : 'editTheirProfile'),
                icon: <PencilIcon />,
                onClick: () =>
                  navigate(
                    isMyProfile
                      ? PROFILE_ROUTE
                      : getEditUserRoute(colleague.userId),
                  ),
              },
            ]
          : []),
        ...((isSuperAdmin || isOwner) && !isColleagueOwner && openTransferDrawer
          ? [
              {
                label: t('transferOwnership'),
                icon: <TransferArrowsIcon />,
                onClick: () => openTransferDrawer(colleague),
              },
            ]
          : []),
        ...(!isDisabled && !isColleagueOwner
          ? [
              {
                label: t('removeFromThisDeal'),
                icon: <TrashIcon />,
                onClick: () => onDeleteColleague(colleague),
              },
            ]
          : []),
      ];

      if (!options.length) return null;

      return (
        <ThreeDotsDropdown
          renderAsPortal
          withBorder={false}
          alignment={ALIGNMENT.VERTICAL}
          portalClassName="colleague-actions-portal"
          ref={dropdownRef}
          disabled={isDisabled}
          options={options}
        />
      );
    },
    [
      isDisabled,
      isSuperAdmin,
      currentUserId,
      isOwner,
      t,
      navigate,
      onDeleteColleague,
      openTransferDrawer,
    ],
  );

  return (
    <div className="colleagues-manage-table-wrapper">
      <table className={classes}>
        <thead>
          <tr>
            {TABLE_COLUMNS.map((col) => (
              <th key={col}>
                <div>
                  {col !== ACTIONS_COLUMN && (
                    <span>{t((COLLEAGUE_PROPS_COPY as any)[col] || col)}</span>
                  )}
                  {(COLLEAGUE_PROPS_TOOLTIP_KEYS as any)[col] && (
                    <ResolvePopup
                      onHover
                      popupContent={
                        <span>
                          {t((COLLEAGUE_PROPS_TOOLTIP_KEYS as any)[col])}
                        </span>
                      }
                    >
                      <InformationcircleIcon />
                    </ResolvePopup>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {colleagues.map((colleague) => (
            <tr key={colleague.email}>
              {TABLE_COLUMNS.map((col) => {
                const isDisabledCheckbox =
                  isDisabled ||
                  (col === 'dataRoomAdmin' && colleague.isOwner) ||
                  (col === 'showOnListing' && !hasListing);

                return (
                  <td key={col}>
                    {col === MEMBER_COLUMN_STRING ? (
                      RenderColleagueInfo(colleague)
                    ) : col !== ACTIONS_COLUMN ? (
                      <Checkbox
                        checked={
                          colleague[col as ColleaguePropertyKeys] as boolean
                        }
                        disabled={isDisabledCheckbox}
                        onChange={() =>
                          changeColleagueValues(
                            colleague.id,
                            COLLEAGUE_PROP_KEYS.reduce(
                              (acc, prop) => ({
                                ...acc,
                                [prop]:
                                  prop === col
                                    ? !colleague[prop]
                                    : colleague[prop],
                              }),
                              {},
                            ) as Record<any, boolean>,
                          )
                        }
                      />
                    ) : (
                      RenderColleagueOptions(colleague)
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>

        {isRequestLoading && <Loader />}
      </table>
    </div>
  );
};

export default ColleaguesManageTable;
